import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Chip, Divider, Grid, Link, Paper, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { formatCurrency, percentFromString, reverseFormatCurrency } from 'helpers';
import moment from 'moment';
import {
  FeatureSubscriptionPaymentCycle,
  FeatureSubscriptionProductCode,
  useCoupon_LoadStripeCouponLazyQuery,
  useFeatureSubscription_CreatePendingFeatureSubscriptionLazyQuery,
  useFeatureSubscription_GetProductPriceLazyQuery,
  useFeatureSubscription_GetStripeSubscriptionProrationLazyQuery,
  useFeatureSubscription_UpdateStripeSubscriptionMutation,
} from 'queries';

import Spinner from 'common/spinner';
import {
  getNameForProduct,
  getProductCodeForCurrentNodeEnvironment,
  getProductCodeForProduction,
} from 'utils/feature-subscription';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  selectedPayCycle: FeatureSubscriptionPaymentCycle;
  selectedPlan: FeatureSubscriptionProductCode;
  selectedAdditionalSeatQuantity: number;
  confirmingPayment: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setConfirmingPayment: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setClientSecret: Function;
  isUpdating: boolean;
};

/**
 * '&&' is required to override the .MuiGrid-root styles.
 * Unknown what is triggering the .MuiGrid-root styles to
 * override the makeStyles's level of specificity.
 */
const useStyles = makeStyles((theme) => ({
  applyDiscountButton: {
    marginLeft: '10px',
  },
  button: {
    width: '100%',
    '&&': {
      height: '25px',
      borderRadius: '15px',
      textTransform: 'none',
    },
  },
  checkoutButtonContainer: {
    '&&': {
      [theme.breakpoints.down('sm')]: {
        position: 'relative',
        borderLeft: '10px',
      },
      borderRadius: '15px',
      margin: '10px 10px 10px 30px',
      minWidth: '300px',
      paddingBottom: '10px',
    },
  },
  chip: {
    marginLeft: '10px',
  },
  chipContainer: {
    width: '100%',
  },
  column1: {
    justifyContent: 'center',
    '& > *': {
      paddingLeft: '10px',
    },
  },
  column2: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    paddingRight: '10px',
  },
  discountContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '8px',
  },
  discountLink: {
    '&&': {
      paddingLeft: '16px',
    },
  },
  item: {
    '&&': {
      width: 'calc(100% - 20px)',
      margin: '0 10px',
      minHeight: 'fit-content',
    },
  },
  paper: {
    '&&': {
      [theme.breakpoints.down('sm')]: {
        position: 'relative',
        borderLeft: '10px',
      },
      borderRadius: '15px',
      margin: '10px 10px 10px 30px',
      minWidth: '300px',
      paddingBottom: '10px',
    },
  },
  textField: {
    '&&': {
      width: '100%',
    },
  },
}));

const CartSummary = ({
  selectedPayCycle,
  selectedPlan,
  selectedAdditionalSeatQuantity,
  confirmingPayment,
  setConfirmingPayment,
  setClientSecret,
  isUpdating,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();
  const [cartTotal, setCartTotal] = useState('');
  const [couponId, setCouponId] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponAmountOff, setCouponAmountOff] = useState<string | undefined>(undefined);
  const [couponPercentOff, setCouponPercentOff] = useState<string | undefined>(undefined);
  const [couponDescription, setCouponDescription] = useState('');
  const [selectedPlanPrice, setSelectedPlanPrice] = useState('');
  const [selectedAdditionalSeatPrice, setSelectedAdditionalSeatPrice] = useState('');

  const implementationFee = 25000;
  const dateNow = moment();
  const dateInOneMonth = dateNow.clone().add(1, 'months');
  const dateInOneYear = dateNow.clone().add(1, 'years');

  const [loadStripeCoupon] = useCoupon_LoadStripeCouponLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const stripeCouponResult = data.data;
      if (stripeCouponResult && stripeCouponResult.description) {
        // Check coupon's product list against selected plan
        if (stripeCouponResult.productList) {
          const productCode = getProductCodeForCurrentNodeEnvironment(selectedPlan) || '';
          const implementationFeeProductCode = getProductCodeForCurrentNodeEnvironment(
            FeatureSubscriptionProductCode.ImplementationFee,
          );

          //If the product list of the coupon is empty, it can be applied to any product
          if (stripeCouponResult.productList.length !== 0) {
            if (
              !stripeCouponResult.productList.includes(productCode) &&
              !stripeCouponResult.productList.includes(implementationFeeProductCode!)
            ) {
              showToast({
                message: 'Invalid coupon for selected plan.',
                severity: NotificationSeverity.WARNING,
              });
              return;
            }
          }
        }
        setCouponId(stripeCouponResult.stripeCouponId);
        setCouponDescription(stripeCouponResult.description);
        if (stripeCouponResult.amountOff) {
          // Flat-rate
          showToast({
            message: `Coupon code applied.`,
            severity: NotificationSeverity.SUCCESS,
          });
          setCouponAmountOff(formatCurrency(stripeCouponResult.amountOff));
        } else if (stripeCouponResult.percentOff) {
          // Percentage off
          if (
            selectedPayCycle === FeatureSubscriptionPaymentCycle.Yearly &&
            stripeCouponResult.durationInMonths &&
            stripeCouponResult.durationInMonths > 1
          ) {
            // Prevent a % off coupon from applying to a yearly subscription when its duration is > 1.
            // TRU-747 for details
            showToast({
              message: 'Invalid coupon for yearly subscriptions.',
              severity: NotificationSeverity.WARNING,
            });
          } else {
            showToast({
              message: `Coupon code applied.`,
              severity: NotificationSeverity.SUCCESS,
            });
            setCouponPercentOff(stripeCouponResult.percentOff!);
          }
        }
      }
    },
    onError: (e) => {
      showAlert({
        message: 'Error loading coupon: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [createPendingFeatureSubscription, { loading: createPendingFeatureSubscriptionExecuting }] =
    useFeatureSubscription_CreatePendingFeatureSubscriptionLazyQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const result = data.data;
        if (result && result.output) {
          setClientSecret(result.output);
        }
      },
      onError: (e) => {
        showAlert({
          message: 'Error creating cart: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [loadProductPriceData] = useFeatureSubscription_GetProductPriceLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const result = data.data;
      if (!result || !result.monthlyPrice) {
        showAlert({
          message: 'Unable to find the product pricing information.',
          severity: NotificationSeverity.ERROR,
        });
        return;
      }

      if (result.annualPrice && selectedPayCycle === FeatureSubscriptionPaymentCycle.Yearly) {
        setSelectedPlanPrice(result.annualPrice);
      } else {
        setSelectedPlanPrice(result.monthlyPrice);
      }
    },
    onError: (e) => {
      showAlert({
        message: 'Error finding the product pricing information: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [loadProratedInvoice, { loading: proratedInvoiceLoading }] =
    useFeatureSubscription_GetStripeSubscriptionProrationLazyQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        setCartTotal(formatCurrency(data.data.output));
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting stripe subscription proration: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [
    updateFeatureSubscriptionMutation,
    { loading: updateFeatureSubscriptionMutationExecuting },
  ] = useFeatureSubscription_UpdateStripeSubscriptionMutation({
    onCompleted: (data) => {
      if (!data.data || !data.data.ok) {
        showAlert({
          message: 'Error updating stripe subscription',
          severity: NotificationSeverity.ERROR,
        });
        return;
      }

      showToast({
        message: 'Subscription has been updated.',
        severity: NotificationSeverity.SUCCESS,
      });
      history.push(`/home`);
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating stripe subscription: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleOnDiscountKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      handleOnApplyDiscountClick();
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnDiscountTextChange = (event: any) => {
    setCouponCode(event.target.value);
  };

  const handleOnApplyDiscountClick = async () => {
    await loadStripeCoupon({
      variables: {
        input: {
          portalId,
          promoCode: couponCode,
        },
      },
    });
  };

  const handleOnDiscountDelete = () => {
    if (confirmingPayment) {
      showToast({
        message: "Click 'Back' to change your cart.",
        severity: NotificationSeverity.WARNING,
      });
      return;
    }
    // Remove the coupon and discount when toggling pay cycle
    // TRU-747 for more details
    setCouponPercentOff(undefined);
    setCouponAmountOff(undefined);
    setCouponId('');
  };

  const handleOnCheckoutClick = () => {
    if (isUpdating) {
      updateFeatureSubscriptionMutation({
        variables: {
          input: {
            portalId,
            selectedPlan,
            additionalSeats: Number(selectedAdditionalSeatQuantity),
          },
        },
      });
    } else {
      if (!confirmingPayment) {
        createPendingFeatureSubscription({
          variables: {
            input: {
              portalId,
              selectedPlan,
              selectedPayCycle,
              additionalSeats: Number(selectedAdditionalSeatQuantity),
              couponId,
            },
          },
        });
      }
      setConfirmingPayment(!confirmingPayment);
    }
  };

  const handleOnHaveDiscountLinkClick = () => {
    setConfirmingPayment(!confirmingPayment);
  };

  useEffect(() => {
    // Remove the coupon and discount when toggling pay cycle or changing plan type
    // TRU-747 for more details
    setCouponId('');
    setCouponPercentOff(undefined);
    setCouponAmountOff(undefined);

    if (isUpdating) {
      loadProratedInvoice({
        variables: {
          input: {
            portalId,
            selectedPlan,
            additionalSeats: Number(selectedAdditionalSeatQuantity),
          },
        },
      });
    } else {
      if (selectedPlan && selectedPayCycle) {
        loadProductPriceData({
          variables: {
            input: {
              portalId,
              payCycle: selectedPayCycle,
              productCode: selectedPlan,
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan, selectedPayCycle, selectedAdditionalSeatQuantity]);

  useEffect(() => {
    if (selectedPayCycle === FeatureSubscriptionPaymentCycle.Yearly) {
      const additionalSeatPrice = 7900 * 12 * selectedAdditionalSeatQuantity;
      setSelectedAdditionalSeatPrice(formatCurrency(`${additionalSeatPrice}`));
    } else {
      const additionalSeatPrice = 7900 * selectedAdditionalSeatQuantity;
      setSelectedAdditionalSeatPrice(formatCurrency(`${additionalSeatPrice}`));
    }
  }, [selectedAdditionalSeatQuantity, selectedPayCycle]);

  useEffect(() => {
    if (!isUpdating) {
      let total =
        reverseFormatCurrency(selectedPlanPrice) +
        reverseFormatCurrency(selectedAdditionalSeatPrice);
      if (couponAmountOff && !couponPercentOff) {
        total = total - reverseFormatCurrency(couponAmountOff);
      } else if (couponPercentOff) {
        setCouponAmountOff(formatCurrency(`${total * percentFromString(couponPercentOff)}`));
        // prettier-ignore
        total = total - (total * percentFromString(couponPercentOff));
      }
      if (selectedPlan !== getProductCodeForProduction(FeatureSubscriptionProductCode.TrialPlan)) {
        total = total + implementationFee;
      }
      setCartTotal(formatCurrency(`${total}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanPrice, selectedAdditionalSeatPrice, couponAmountOff, couponPercentOff]);

  if (proratedInvoiceLoading) {
    return <Spinner />;
  }

  const shouldChargeImplementationFee = () => {
    return (
      !isUpdating &&
      selectedPlan !== getProductCodeForProduction(FeatureSubscriptionProductCode.TrialPlan)
    );
  };

  return (
    <>
      <Paper className={classes.paper} elevation={3}>
        <Grid>
          <Grid container spacing={1}>
            <Grid item justifyContent="center" className={classes.column1}>
              <Typography variant="h6">
                <strong>{isUpdating ? 'Upgraded Plan Summary' : 'Plan Summary'}</strong>
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} className={classes.column1}>
              <Typography variant="subtitle1">{getNameForProduct(selectedPlan)}</Typography>
            </Grid>
            <Grid item xs={12} md={4} className={classes.column2}>
              <Typography variant="subtitle1">{selectedPlanPrice}</Typography>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          {selectedAdditionalSeatQuantity > 0 && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8} className={classes.column1}>
                  <Typography variant="subtitle1">Additional Seats</Typography>
                  <Typography variant="subtitle1">
                    {selectedAdditionalSeatQuantity ? `x ${selectedAdditionalSeatQuantity}` : ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} className={classes.column2}>
                  <Typography variant="subtitle1">{selectedAdditionalSeatPrice}</Typography>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
            </>
          )}

          {shouldChargeImplementationFee() && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8} className={classes.column1}>
                  <Typography variant="subtitle1">Implementation Fee</Typography>
                </Grid>
                <Grid item xs={12} md={4} className={classes.column2}>
                  <Typography variant="subtitle1">$250.00</Typography>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
            </>
          )}
          {couponAmountOff && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8} className={classes.column1}>
                  <Typography variant="subtitle1">Discount:</Typography>
                  <Typography variant="subtitle2">{couponDescription}</Typography>
                </Grid>
                <Grid item xs={12} md={4} className={classes.column2}>
                  {couponPercentOff ? (
                    <Typography variant="subtitle1">
                      ({couponPercentOff}%) - {couponAmountOff}
                    </Typography>
                  ) : (
                    <Typography variant="subtitle1">-{couponAmountOff}</Typography>
                  )}{' '}
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
            </>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} className={classes.column1}>
              <Typography variant="subtitle1">
                {isUpdating ? 'Prorated Invoice' : 'Total due today'}
                {isUpdating ? (
                  <>
                    {' '}
                    <Typography variant="subtitle1">(Next invoice)</Typography>
                  </>
                ) : (
                  <>
                    {shouldChargeImplementationFee() && (
                      <Typography variant="subtitle2">
                        Renews:{' '}
                        {selectedPayCycle === FeatureSubscriptionPaymentCycle.Yearly
                          ? dateInOneYear.format('MMMM, D YYYY')
                          : dateInOneMonth.format('MMMM, D YYYY')}
                      </Typography>
                    )}
                  </>
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} className={classes.column2}>
              <Typography variant="subtitle1">{cartTotal}</Typography>
            </Grid>
          </Grid>
          {!isUpdating && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Grid>
          )}
          {!isUpdating &&
            !confirmingPayment &&
            (!couponAmountOff && !couponPercentOff ? (
              <Grid container spacing={1} className={classes.discountContainer}>
                <Grid item className={clsx(classes.column1)} xs={12} md={8}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Discount code"
                    value={couponCode}
                    className={classes.textField}
                    onChange={handleOnDiscountTextChange}
                    onKeyPress={handleOnDiscountKeyPress}
                  />
                </Grid>
                <Grid item className={clsx(classes.column2)} xs={12} md={4}>
                  <Button
                    variant="contained"
                    size="medium"
                    color="primary"
                    disabled={couponCode === ''}
                    className={clsx(classes.applyDiscountButton, classes.button)}
                    onClick={handleOnApplyDiscountClick}
                  >
                    Apply
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1} className={classes.discountContainer}>
                <Grid item className={clsx(classes.column1)} xs={12} md={6}>
                  <Typography variant="subtitle1">Discount applied: </Typography>
                </Grid>
                <Grid item className={clsx(classes.column2, classes.chipContainer)} xs={12} md={6}>
                  <Chip
                    label={couponCode}
                    onDelete={handleOnDiscountDelete}
                    className={classes.chip}
                  />
                </Grid>
              </Grid>
            ))}
          {confirmingPayment &&
            (!couponAmountOff && !couponPercentOff ? (
              <Grid container spacing={1}>
                <Grid item xs={12} md={12}>
                  <Link onClick={handleOnHaveDiscountLinkClick}>
                    <ul className={classes.discountLink}>Have a discount code?</ul>
                  </Link>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1} className={classes.discountContainer}>
                <Grid item className={clsx(classes.column1)} xs={12} md={6}>
                  <Typography variant="subtitle1">Discount applied: </Typography>
                </Grid>
                <Grid item className={clsx(classes.column2, classes.chipContainer)} xs={12} md={6}>
                  <Chip
                    label={couponCode}
                    onDelete={handleOnDiscountDelete}
                    className={classes.chip}
                  />
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Paper>
      <div className={classes.checkoutButtonContainer}>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          disabled={
            createPendingFeatureSubscriptionExecuting || updateFeatureSubscriptionMutationExecuting
          }
          className={clsx(classes.button)}
          onClick={handleOnCheckoutClick}
        >
          {isUpdating ? 'Upgrade' : !confirmingPayment ? 'Checkout' : 'Back'}
        </Button>
      </div>
    </>
  );
};

export default CartSummary;
